import React, { useRef, useState, type ReactElement, type ReactNode } from "react";
import { Popover, PopoverContent, PopoverTrigger, Skeleton, cn } from "@ndd/ui";
import type {
  HoursCell,
  HoursLegendItem,
  NoNextClient,
  WeekSummary as WeekSummaryModel,
} from "../../lib/rd-schedule-week.js";

// NO-874: the Week view's summary row ("This week"): client-facing hours
// against the contract, project hours, S codes and the no-next-visit count.
// Done hours are the solid segment and booked hours the lighter one; the
// contract being hit gets the theme's lime highlight and being short is plain
// facts, never red. The legend under the bar names each segment by its colour:
// done and booked add up to the big number, and a short week adds what is
// still to go to reach the contract (past it, "over" is plain text with no
// swatch). The no-next-visit cell names the Clients, each opening their latest
// visit this week; below sm it is the count and a "See who" list instead.
// Every cell has one anatomy: a label, the number, and at most one supporting
// line, so the row reads as one calm band. Presentational, relative imports
// only.

// Each cell spans the row's three tracks and subgrids them (label / number /
// note), so the numbers line up across cells even when a label wraps.
const CELL =
  "relative row-span-3 grid min-w-0 grid-rows-subgrid gap-y-1 px-[22px] max-sm:px-3 sm:max-lg:px-4";
// The divider spans the content, inset a little, not the card's full height.
const DIVIDER =
  "before:absolute before:inset-y-1 before:left-0 before:w-px before:bg-border";
const LABEL = "text-label text-muted-foreground";
const NUMBER =
  "font-sans text-heading-sm font-bold leading-[1.1] tabular-nums text-foreground";
const NOTE = "text-meta text-muted-foreground";
const LINK =
  "text-meta font-semibold text-primary underline decoration-1 underline-offset-[3px]";
// Each swatch is its bar segment's colour; "to book" and "short" are the track.
// "over" has no segment, so no swatch.
const SWATCH: Record<HoursLegendItem["kind"], string | null> = {
  done: "bg-primary",
  booked: "bg-primary/50",
  to_book: "bg-secondary",
  short: "bg-secondary",
  over: null,
};
const LIME = {
  backgroundImage: "linear-gradient(transparent 55%, var(--color-highlight) 55%)",
};

/** The summary row's state: the answer in hand, still loading, or unreadable. */
export type WeekSummaryState =
  | { kind: "ready"; summary: WeekSummaryModel }
  | { kind: "loading" }
  | { kind: "unavailable" };

export interface WeekSummaryProps {
  state: WeekSummaryState;
  /** A no-next-visit Client's name was chosen: open that visit's details. */
  onSelectClient: (appointmentId: string) => void;
  onSetHours: (() => void) | null;
  /** Opens another week (NO-907: the week the saved hours start). */
  onOpenWeek: (week: string) => void;
}

export function WeekSummaryRow(props: WeekSummaryProps): ReactElement {
  const { state } = props;
  const summary = state.kind === "ready" ? state.summary : null;

  return (
    <section
      aria-label="This week"
      className="mb-3.5 grid grid-cols-3 rounded-xl border border-border bg-card py-3 sm:grid-cols-[minmax(0,2.4fr)_repeat(3,minmax(0,1fr))]"
    >
      <div
        className={cn(
          CELL,
          "col-span-3 max-sm:border-b max-sm:border-border max-sm:pb-3 sm:col-span-1"
        )}
      >
        {summary ? (
          <HoursCellView
            cell={summary.hours}
            onSetHours={props.onSetHours}
            onOpenWeek={props.onOpenWeek}
          />
        ) : state.kind === "loading" ? (
          <HoursLoading />
        ) : (
          <HoursUnavailable />
        )}
      </div>
      <Stat
        label="Project hours"
        value={summary ? summary.projectHours : "–"}
        note={summary ? summary.projectNote : null}
        className="max-sm:pt-3 max-sm:before:hidden"
      />
      <Stat
        label="S codes"
        value={summary ? summary.sCodes : "–"}
        note={summary ? summary.sCodeNote : null}
        className="max-sm:pt-3"
      />
      <div className={cn(CELL, DIVIDER, "max-sm:pt-3")}>
        <span className={LABEL}>No next visit</span>
        {summary ? (
          <NoNextVisit noNext={summary.noNextVisit} onSelect={props.onSelectClient} />
        ) : (
          <>
            <span className={cn(NUMBER, "text-subtle")}>–</span>
            <span className={NOTE} aria-hidden="true">
              {"\u00a0"}
            </span>
          </>
        )}
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  note,
  className,
}: {
  label: string;
  value: ReactNode;
  note: string | null;
  className?: string;
}): ReactElement {
  return (
    <div className={cn(CELL, DIVIDER, className)}>
      <span className={LABEL}>{label}</span>
      <span className={NUMBER}>{value}</span>
      <span className={NOTE}>{note ?? "\u00a0"}</span>
    </div>
  );
}

const NAMES_SHOWN = 2;

export function NoNextVisit({
  noNext,
  onSelect,
}: {
  noNext: WeekSummaryModel["noNextVisit"];
  onSelect: (appointmentId: string) => void;
}): ReactElement {
  const shown = noNext.clients.slice(0, NAMES_SHOWN);
  const more = noNext.clients.slice(NAMES_SHOWN);

  return (
    <>
      <span className={cn(NUMBER, noNext.hasAny && "text-destructive")}>
        {noNext.text}
      </span>
      {noNext.hasAny ? (
        <>
          <span
            className={cn(
              NOTE,
              "hidden min-w-0 items-center gap-x-2 whitespace-nowrap sm:flex"
            )}
          >
            {shown.map((client) => (
              <NameLink
                key={client.patientId}
                client={client}
                onSelect={onSelect}
                className="min-w-0 shrink truncate"
              />
            ))}
            {more.length > 0 ? (
              <NamesPopover
                clients={more}
                onSelect={onSelect}
                label={`${more.length} more ${more.length === 1 ? "client" : "clients"} with no next visit`}
                className="shrink-0"
              >
                {`and ${more.length} more`}
              </NamesPopover>
            ) : null}
          </span>
          {/* The cell is about 90px wide on a phone: no room for names. */}
          <span className={cn(NOTE, "flex min-w-0 sm:hidden")}>
            <NamesPopover
              clients={noNext.clients}
              onSelect={onSelect}
              label={`See who: ${noNext.clients.length} ${noNext.clients.length === 1 ? "client" : "clients"} with no next visit`}
            >
              See who
            </NamesPopover>
          </span>
        </>
      ) : (
        <span className={NOTE}>Everyone has a next visit</span>
      )}
    </>
  );
}

export function NameLink({
  client,
  onSelect,
  className,
}: {
  client: NoNextClient;
  onSelect: (appointmentId: string) => void;
  className?: string;
}): ReactElement {
  return (
    <button
      type="button"
      title={client.name}
      onClick={() => onSelect(client.appointmentId)}
      className={cn(LINK, "-my-1 min-h-6 py-1 text-left", className)}
    >
      {client.name}
      <span className="sr-only">, open latest visit</span>
    </button>
  );
}

/** A button that opens a small list of Clients; choosing one closes it and opens their visit. */
function NamesPopover({
  clients,
  onSelect,
  label,
  className,
  children,
}: {
  clients: NoNextClient[];
  onSelect: (appointmentId: string) => void;
  label: string;
  className?: string;
  children: ReactNode;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const picked = useRef<string | null>(null);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={trigger}
          type="button"
          aria-label={label}
          className={cn(LINK, "-my-1 min-h-6 py-1", className)}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label="Clients with no next visit"
        className="w-64 p-2"
        // The details panel is modal and returns focus to whatever had it when it
        // opened. Opening it in the same commit as the close would find the page,
        // so focus goes back to this button first, then the visit opens.
        onCloseAutoFocus={(event) => {
          const appointmentId = picked.current;

          picked.current = null;

          if (appointmentId === null) return;

          event.preventDefault();
          trigger.current?.focus();
          onSelect(appointmentId);
        }}
      >
        <NoNextList
          clients={clients}
          onSelect={(appointmentId) => {
            picked.current = appointmentId;
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export function NoNextList({
  clients,
  onSelect,
}: {
  clients: NoNextClient[];
  onSelect: (appointmentId: string) => void;
}): ReactElement {
  return (
    <ul className="flex flex-col">
      {clients.map((client) => (
        <li key={client.patientId} className="flex min-w-0">
          <NameLink
            client={client}
            onSelect={onSelect}
            className="min-w-0 break-words"
          />
        </li>
      ))}
    </ul>
  );
}

function HoursCellView({
  cell,
  onSetHours,
  onOpenWeek,
}: {
  cell: HoursCell;
  onSetHours: (() => void) | null;
  onOpenWeek: (week: string) => void;
}): ReactElement {
  if (cell.kind === "no_hours") {
    return (
      <TallyCell big={cell.big}>
        {onSetHours ? (
          <>
            <button type="button" onClick={onSetHours} className={LINK}>
              Set your hours
            </button>{" "}
            {cell.hasContract
              ? "to track against your contract."
              : "to see how your week is tracking."}
          </>
        ) : (
          "No weekly hours saved yet."
        )}
      </TallyCell>
    );
  }

  if (cell.kind === "starts_later") {
    return (
      <TallyCell big={cell.big}>
        Your hours start {cell.label}.{" "}
        <button type="button" onClick={() => onOpenWeek(cell.week)} className={LINK}>
          Go to that week
        </button>
      </TallyCell>
    );
  }

  if (cell.kind === "no_contract") {
    return (
      <TallyCell big={cell.big}>
        No contracted hours on file yet. Ask your supervisor to add them.
      </TallyCell>
    );
  }

  return <HoursBar cell={cell} />;
}

/** The done-and-booked tally with no target to measure against, and one line saying why. */
function TallyCell({
  big,
  children,
}: {
  big: string;
  children: ReactNode;
}): ReactElement {
  return (
    <>
      <span className={LABEL}>Client-facing hours</span>
      <span className="flex flex-wrap items-baseline gap-x-2">
        <span className={NUMBER}>{big}</span>
        <span className={NOTE}>done and booked</span>
      </span>
      <span className={cn(NOTE, "text-muted-strong")}>{children}</span>
    </>
  );
}

function HoursProgress({
  cell,
}: {
  cell: Extract<HoursCell, { kind: "bar" }>;
}): ReactElement {
  return (
    <span
      role="progressbar"
      aria-label="Client-facing hours, done and booked, against contracted hours"
      aria-valuemin={0}
      aria-valuemax={cell.ariaMax}
      aria-valuenow={Math.min(cell.ariaNow, cell.ariaMax)}
      aria-valuetext={cell.ariaLabel}
      className="flex h-1.5 min-w-12 flex-1 self-center overflow-hidden rounded-[3px] bg-secondary"
    >
      <span
        className="block h-full bg-primary"
        style={{ width: `${cell.doneShare}%` }}
      />
      <span
        className="block h-full bg-primary/50"
        style={{ width: `${cell.bookedShare}%` }}
      />
    </span>
  );
}

function HoursBar({
  cell,
}: {
  cell: Extract<HoursCell, { kind: "bar" }>;
}): ReactElement {
  return (
    <>
      <span className={LABEL}>Client-facing hours</span>
      <span className="flex items-baseline gap-x-2">
        <span className={NUMBER}>{cell.big}</span>
        <span className={cn(NOTE, "whitespace-nowrap")}>{cell.of}</span>
        <HoursProgress cell={cell} />
      </span>
      <span
        className={cn(
          NOTE,
          "flex flex-wrap items-center gap-x-3 gap-y-0.5 text-muted-strong"
        )}
      >
        {cell.word ? (
          <span
            className={cn("font-semibold", cell.hit && "px-0.5 text-primary")}
            style={cell.hit ? LIME : undefined}
          >
            {cell.word}
          </span>
        ) : null}
        {cell.legend.map((item, at) => (
          <span key={item.kind} className="inline-flex items-center gap-1.5">
            {SWATCH[item.kind] ? (
              <span
                aria-hidden="true"
                className={cn("size-2 rounded-[2px]", SWATCH[item.kind])}
              />
            ) : null}
            {item.text}
            {/* Screen readers hear the items as a list, not one run of words. */}
            {at < cell.legend.length - 1 ? <span className="sr-only">, </span> : null}
          </span>
        ))}
        {cell.note ? <span>{cell.note}</span> : null}
      </span>
    </>
  );
}

function HoursLoading(): ReactElement {
  return (
    // The grid's skeleton announces the load; this cell only mirrors it.
    <>
      <span aria-hidden="true" className={LABEL}>
        Client-facing hours
      </span>
      <Skeleton aria-hidden="true" className="h-[24px] w-[120px] rounded-lg" />
      <span aria-hidden="true" className={NOTE}>
        Loading this week…
      </span>
    </>
  );
}

/** The grid card under the row carries the one Try again. */
function HoursUnavailable(): ReactElement {
  return (
    <>
      <span className={LABEL}>Client-facing hours</span>
      <span className={cn(NUMBER, "text-subtle")}>–</span>
      <span className={cn(NOTE, "text-muted-strong")}>
        Hours unavailable right now.
      </span>
    </>
  );
}
